import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { APIResponse, DropdownOption, DropdownData } from './predict-interface';
import { DynamicTableService } from 'src/app/modules/shared/services/dynamic-table/dynamic-table.service';
import { finalize } from 'rxjs/operators';
import { ColumnDefinition } from 'src/app/modules/shared/models/dynamic-table/dynamic-table-data.model';
import { TimeZoneService } from 'src/app/modules/shared/services/timezone.service';
import { SavedSearchesService } from '../../services/saved-searches.service';

@Component({
  selector: 'app-predict-dialog',
  templateUrl: './predict-dialog.component.html',
  styleUrl: './predict-dialog.component.scss'
})
export class PredictDialogComponent implements OnInit {
  @Input() visible: boolean = false;
  @Input() columns: string[] = [];
  @Input() workbookId?: string;
  @Input() targetColumn: any;
  @Input() columnDefinitions?:any[];
  @Input() savedSearchColumn?:any[];
  @Input() SavedSearchId?: number;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saveData = new EventEmitter<any>();
  formId:any

  predictionForm: FormGroup;
  dropdownOptions: DropdownData = {};
  dropdownMappings: { [key: string]: { [key: number]: string } } = {};
  loading: boolean = false;
  userInputRows: any[] = [];
  userPredictionRows: any[] = [];
  inferenceResponse:any={}
  
  constructor(
    private fb: FormBuilder,
    public dynamicTableService: DynamicTableService,
    private readonly timezoneService: TimeZoneService,
    private savedSearchesService: SavedSearchesService,
  ) {
    // this.predictionForm = this.fb.group({});
  }

  private initializeDropdownOptionsForWorkbook() {
    this.dynamicTableService.PredictionModelMapping(this.formId)
      .subscribe((data: APIResponse) => {
        this.dropdownOptions = {};
        this.dropdownMappings = {};
        
        for (const [column, options] of Object.entries(data.mappings)) {
          const columnLower = column.toLowerCase();
          
          // Store the reverse mapping (number to string) for each dropdown
          this.dropdownMappings[columnLower] = {};
          
          this.dropdownOptions[columnLower] = Object.entries(options).map(([label, value]) => {
            const numValue = typeof value === 'number' ? value : parseInt(value);
            // Store the reverse mapping
            this.dropdownMappings[columnLower][numValue] = label;
            return {
              label,
              value: numValue
            };
          });
        }
        this.mapWorkbookColumn(this.columnDefinitions);
      });
  }

  private initializeDropdownOptionsForSavedSearch() {
    this.savedSearchesService.PredictionModelMappingForSavedSearch(this.formId)
      .subscribe((data: APIResponse) => {
        this.dropdownOptions = {};
        this.dropdownMappings = {};
        
        for (const [column, options] of Object.entries(data.mappings)) {
          const columnLower = column.toLowerCase();
          
          // Store the reverse mapping (number to string) for each dropdown
          this.dropdownMappings[columnLower] = {};
          
          this.dropdownOptions[columnLower] = Object.entries(options).map(([label, value]) => {
            const numValue = typeof value === 'number' ? value : parseInt(value);
            // Store the reverse mapping
            this.dropdownMappings[columnLower][numValue] = label;
            return {
              label,
              value: numValue
            };
          });
        }
        this.mapSaveSearchColumn(this.savedSearchColumn);
      });
  }

  ngOnInit() {
   this.formId = this.SavedSearchId || this.workbookId
    if(this.SavedSearchId){
      this.initializeDropdownOptionsForSavedSearch();
    }
    else if(this.workbookId){
      this.initializeDropdownOptionsForWorkbook();
    }
  }

   buildPredictForm(){
    const group: any = {};
    this.columnDefinitions?.forEach(column => {
      const control = this.createControl(column);
      group[column.name] = control;
      });
      this.predictionForm = this.fb.group(group);
   }

  mapSaveSearchColumn(savedSearchColumns:any){
    const targetColumnName= this.targetColumn.header;
    this.columnDefinitions = savedSearchColumns.filter((column) => column.header !== targetColumnName).map((column) => ({
      id: column.id,
      name: column.header,
      dataType: column.fieldDataType,
      label: column.header,
      formControlName: this.dropdownOptions[column.header.toLowerCase()]?'DropDownList': column.formControlType
       // Mapping 'header' to 'label'
    }));
    this.buildPredictForm();
  }

  mapWorkbookColumn(workbookColumn:any){
    const targetColumnName= this.targetColumn.name;
    this.columnDefinitions = workbookColumn.filter((column) => column.name !== targetColumnName).map(column => ({
      ...column,
      formControlName: this.dropdownOptions[column.name.toLowerCase()]
        ? 'DropDownList'
        : column.formControlName, // Modify only formControlName dynamically
    }));
    this.buildPredictForm();
  }

  createControl(column: ColumnDefinition) {
    const validators = [];
    validators.push(Validators.required);  
    return this.fb.control({ value: null, disabled: false }, validators);
  }

  getInputType(column: string): string {

    const columnLower = column.toLowerCase();
    
    if (this.dropdownOptions[columnLower]) {
      return 'dropdown';
    }

    if (columnLower.includes('date')) {
      return 'date';
    }
    
    if (columnLower === 'price' || 
        columnLower === 'charges' || 
        columnLower.includes('amount') || 
        columnLower.includes('total')) {
      return 'currency';
    }
    
    const numberFields = [
      'age', 'children', 'bmi', 'stroke', 'enginesize', 
      'citympg', 'highwaympg', 'wheelbase', 'compressionratio',
      'carlength', 'carwidth', 'carheight', 'curbweight',
      'boreratio', 'horsepower', 'peakrpm'
    ];
    
    if (numberFields.includes(columnLower) || 
        columnLower.includes('quantity') || 
        columnLower.includes('number')) {
      return 'number';
    }

    return 'text';
  }

  getDropdownOptions(column: string): DropdownOption[] {
    const columnLower = column.toLowerCase();
    return this.dropdownOptions[columnLower] || [];
  }

  closeDialog() {
    this.visible = false;
    this.visibleChange.emit(false);
    this.predictionForm.reset();
  }

  private formatFormData(): any {
    const formValues: Record<string, any> = this.predictionForm.value;
    const formattedData: any = {};

    for (const [key, value] of Object.entries(formValues)) {
      const keyLower = key.toLowerCase();
      const columnDef = this.columnDefinitions.find(col => col.name === key);

      if (this.dropdownMappings[keyLower] && typeof value === 'number') {
        // Convert numeric dropdown values back to their string representations
        formattedData[key] = this.dropdownMappings[keyLower][value] || value;
      } else if (this.getInputType(key) === 'currency' || this.getInputType(key) === 'number') {
        // Keep numeric values as is for number fields
        formattedData[key] = value;
      } else if (columnDef.formControlName === 'DateTime' && value) {
        formattedData[key] = this.timezoneService._getCustomDateString(value as string)
      }
      else if (columnDef.formControlName === 'DateTimePicker' && value) {
        formattedData[key] = this.timezoneService._getCustomDateTimeString(value as string)
      }
      else if (columnDef.formControlName === 'DropDownList' && value) {
        formattedData[key] = (value as { label: any }).label;
      }      
      else {
        // Keep string values as is
        formattedData[key] = value;
      }
    }

    return formattedData;
  }

  onSubmit() {
    if (this.predictionForm.valid && this.formId) {
      this.loading = true;
      
      // Format the payload with string values for categorical variables
      const payload = {
        id: this.formId,
        data: JSON.stringify(this.formatFormData())
      };
      if(this.workbookId){
        this.submitDynamicTableInferencing(payload);
      }
      else if(this.SavedSearchId){
        this.submitSaveSearchInferencing(payload);
      }
    }
  }

  submitDynamicTableInferencing(payload){
    this.dynamicTableService.getPredictionModelInference(this.formId, payload)
    .pipe(
      finalize(() => {
        this.loading = false;
      })
    )
    .subscribe({
      next: (response) => {
       // Update userInputRows and userPredictionRows
       const saveUserInputDTInferecingPayload = {
        id:this.formId,
        targetColumn: this.targetColumn?.name,
        data: payload.data,
        prediction: response.prediction
      }
      this.saveUserInputDynamicTable(saveUserInputDTInferecingPayload)
       this.updateInferenceInputs(response, payload.data)
      },
      error: (error) => {
        console.error('Error making prediction:', error);
        // You might want to add a toast message here
      }
    });

  }

  submitSaveSearchInferencing(payload){
    this.savedSearchesService.getPredictionModelInference(this.formId, payload)
    .pipe(
      finalize(() => {
        this.loading = false;
      })
    )
    .subscribe({
      next: (response) => {
        const saveUserInputSaveSearchPayload = {
          id:this.formId,
          targetColumn: this.targetColumn?.header,
          data: payload.data,
          prediction: response.prediction
        }
        this.saveUserInputSaveSearch(saveUserInputSaveSearchPayload)
       // Update userInputRows and userPredictionRows
       this.updateInferenceInputs(response, payload.data);
      },
      error: (error) => {
        console.error('Error making prediction:', error);
        // You might want to add a toast message here
      }
    });

  }

  saveUserInputDynamicTable(payload){
    this.dynamicTableService.userInput(payload)
    .subscribe({
      next: (response) => {
       // Update userInputRows and userPredictionRows  
      },
      error: (error) => {
        console.error('saveUserInputDynamicTable:', error);
        // You might want to add a toast message here
      }
    });    
  }

  saveUserInputSaveSearch(payload){
    this.savedSearchesService.userInput(payload)
    .subscribe({
      next: (response) => {
       // Update userInputRows and userPredictionRows  
      },
      error: (error) => {
        console.error('saveUserInputSaveSearch:', error);
        // You might want to add a toast message here
      }
    });    
  }
  updateInferenceInputs(data:any, prdictData){
    this.userInputRows = [this.predictionForm.value];
    this.inferenceResponse['rowData']= JSON.parse(prdictData);
    this.inferenceResponse['inferenceData'] = [data];
    this.userPredictionRows = data.category?.[0]?.prediction?.value.map(value => ({ value }));
    this.saveData.emit(this.inferenceResponse);
    this.closeDialog();

  }
}